"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import {
  Stack,
  TextInput,
  Button,
  Text,
  Title,
  Paper,
  Badge,
  Group,
} from "@forestsync/ui";
import { TbZoomScan } from "@forestsync/ui";
import { detectRoute } from "@/lib/verify";

const ID_TYPES = [
  { key: "passport", example: "FS050DE…", color: "green" },
  { key: "transaction", example: "FSTX…", color: "blue" },
  { key: "event", example: "FSEVT…", color: "violet" },
  { key: "sink", example: "XX-XXXX", color: "teal" },
  { key: "user", example: "8-char ID", color: "orange" },
] as const;

export function VerifyLookup() {
  const t = useTranslations("glassroom.verify");
  const router = useRouter();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const route = detectRoute(value);
    if (!route) {
      setError(t("errorUnknown"));
      return;
    }
    setError("");
    router.push(route);
  };

  return (
    <Stack gap="xl" align="center" w="100%">
      <Stack align="center" gap="xs" maw={480} ta="center">
        <TbZoomScan size={48} color="var(--mantine-color-violet-6)" />
        <Title order={2} mt={4}>
          {t("title")}
        </Title>
        <Text size="md" c="dimmed">
          {t("description")}
        </Text>
      </Stack>

      <Paper withBorder radius="md" p="xl" w="100%" maw={540}>
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <TextInput
              value={value}
              onChange={(e) => {
                setValue(e.currentTarget.value);
                setError("");
              }}
              placeholder={t("inputPlaceholder")}
              error={error}
              size="lg"
            />
            <Button type="submit" size="lg" fullWidth disabled={!value.trim()}>
              {t("button")}
            </Button>
          </Stack>
        </form>
      </Paper>

      <Stack gap="xs" align="center">
        <Text size="xs" c="dimmed" fw={500} tt="uppercase" lts={1}>
          {t("quickGuideLabel")}
        </Text>
        <Group justify="center" gap="xs">
          {ID_TYPES.map(({ key, example, color }) => (
            <Badge
              key={key}
              variant="light"
              color={color}
              size="md"
              radius="sm"
            >
              {t(`types.${key}`)} · {example}
            </Badge>
          ))}
        </Group>
      </Stack>
    </Stack>
  );
}
